import {
  IconButton,
  Tooltip,
  Zoom,
  Chip,
  Typography,
  Box,
  Button,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import PhoneIcon from '../TrackerFilter/Icons/PhoneIcon';
import SmartphoneIcon from '../TrackerFilter/Icons/SmartPhoneIcon';
import EmailIcon from '../TrackerFilter/Icons/EmailIcon';
import CommentIcon from '../TrackerFilter/Icons/CommentIcon';
import PositiveIcon from '../TrackerFilter/Icons/PositiveIcon';
import NeutralIcon from '../TrackerFilter/Icons/NeutralIcon';
import NegativeIcon from '../TrackerFilter/Icons/NegativeIcon';
import AttachmentIcon from '../TrackerFilter/Icons/AttachmentIcon';
import LinkedServiceIcon from '../TrackerFilter/Icons/LinkedServiceIcon';

import { UploadCell } from './CellTypes';
import ErrorBoundary from '../ErrorBoundary';
import { useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import { Link } from 'react-router-dom';
import {
  ContactMethodName,
  complexityOptions,
  getTrackerViewColor,
  ServiceOrIssueStatus,
  ServiceOrIssueStatusColorMap,
  TrackerDataType,
  mediaToneOptions,
} from '../../shared/constants/constants';
import {
  ContentLogKeys,
  IssueAndServiceSharedValues,
  IssueKeys,
  ServiceKeys,
  inProgressKeys,
  EngagementKeys,
  IssueAndEngagementSharedValues,
} from '../../shared/constants/IssueAndServiceKeys';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { RouteInformation } from '../../shared/constants/RouteInformation';
import { useDialog } from '../../shared/contexts/DialogContext';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddOnProtectedWrapper from '../../shared/protectedWrappers/AddOnProtectedWrapper';
import { AddOnTypes } from '../../shared/constants/TenantFields';
import { WORK_TYPE_COLORS } from '../../shared/constants/workTypeColors';
import dayjs from '../../utils/dayjs';
import { styled } from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MadeByAutoTrackerIcon from '../TrackerFilter/Icons/MadeByAutoTrackerIcon';
import { logPalette, neutralPalette } from '../../theme/palette';
import {
  adderHeaderIconTileSx,
  adderHeaderTitleSx,
  queryAdderPopupEyebrowSx,
  queryAdderPopupContentSx,
  queryAdderPopupHeaderSx,
  queryAdderPopupPaperSx,
  utilityModalFooterSx,
  utilityPrimaryActionButtonSx,
  utilityCancelButtonSx,
} from '../QueryAdder/queryAdderPopupStyles';
import { getSidebarLogIconComponent } from '../../shared/constants/logIcons';

// Styled tooltip component for "Click to Copy" tooltips
const WhiteBackgroundTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '8px 10px',
    gap: '8px',
    boxShadow: '0px 0px 4px rgba(126, 126, 126, 0.25)',
    borderRadius: '5px',
  },
  [`& .MuiTooltip-arrow`]: {
    color: 'white',
  },
}));

const TooltipTitle = styled(Typography)({
  fontWeight: 400,
  fontSize: '12px',
  color: '#A3A3A3',
});

const TooltipContent = styled(Typography)({
  fontSize: '14px',
  color: 'black',
});

const ClickToCopyTooltip = ({ content, icon }) => {
  const displayContent = content || 'N/A';

  return (
    <WhiteBackgroundTooltip
      title={
        <Box>
          <TooltipTitle>Click to copy</TooltipTitle>
          <TooltipContent>{displayContent}</TooltipContent>
        </Box>
      }
      arrow
    >
      <IconButton
        style={{ padding: 4 }}
        onClick={() => navigator.clipboard.writeText(displayContent)}
        size="large"
        // Visual indication when no content available
        sx={!content ? { opacity: 0.5 } : {}}
      >
        {icon}
      </IconButton>
    </WhiteBackgroundTooltip>
  );
};

const ServiceLogIcon = getSidebarLogIconComponent(TrackerDataType.SERVICE);
const MediaInteractionLogIcon = getSidebarLogIconComponent(TrackerDataType.MEDIA_INTERACTION);
const EngagementLogIcon = getSidebarLogIconComponent(TrackerDataType.ENGAGEMENT);
const ContentLogIcon = getSidebarLogIconComponent(TrackerDataType.CONTENT_LOG);

const stripHtmlTagsFromString = (value) => {
  let plainText = '';
  let isInsideTag = false;

  for (const character of value) {
    if (character === '<') {
      isInsideTag = true;
      plainText += ' ';
      continue;
    }

    if (character === '>') {
      isInsideTag = false;
      continue;
    }

    if (!isInsideTag) {
      plainText += character;
    }
  }

  return plainText;
};

const normalizeSpacesBeforeNewlines = (value) => {
  let normalized = '';
  let pendingSpaces = '';

  for (const character of value) {
    if (character === ' ' || character === '\t') {
      pendingSpaces += character;
      continue;
    }

    if (character === '\n') {
      normalized += '\n';
      pendingSpaces = '';
      continue;
    }

    normalized += pendingSpaces + character;
    pendingSpaces = '';
  }

  return normalized + pendingSpaces;
};

const getPlainTextFromRichText = (value = '') => {
  if (!value) {
    return '';
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return stripHtmlTagsFromString(String(value)).replaceAll(/\s+/g, ' ').trim();
  }

  const element = document.createElement('div');
  element.innerHTML = value;

  element.querySelectorAll('br').forEach((br) => {
    br.replaceWith('\n');
  });

  element.querySelectorAll('p, div, li').forEach((block) => {
    block.appendChild(document.createTextNode('\n'));
  });

  return normalizeSpacesBeforeNewlines(element.textContent.replaceAll('\u00a0', ' '))
    .replaceAll(/\n{3,}/g, '\n\n')
    .trim();
};

const trimToWordBoundary = (value) => {
  const trimmed = value.trimEnd();

  if (trimmed.length < 24) {
    return trimmed;
  }

  const lastSpaceIndex = trimmed.lastIndexOf(' ');

  if (lastSpaceIndex < trimmed.length - 24) {
    return trimmed;
  }

  return trimmed.slice(0, lastSpaceIndex).trimEnd();
};

const LongTextPreview = ({ data, isExpandedView, onReadMore }) => {
  const previewRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpandedTruncated, setIsExpandedTruncated] = useState(false);
  const [expandedPreviewText, setExpandedPreviewText] = useState('');
  const maxPreviewHeight = isExpandedView ? 160 : 130;
  const expandedLineHeightPx = 16;
  const inlineReadMorePrefix = '… ';

  useEffect(() => {
    const checkOverflow = () => {
      const node = previewRef.current;
      if (!node) {
        setIsOverflowing(false);
        setIsExpandedTruncated(false);
        setExpandedPreviewText('');
        return;
      }

      if (!isExpandedView) {
        const nextOverflowing = node.scrollHeight > node.clientHeight + 1;
        setIsOverflowing(nextOverflowing);
        setIsExpandedTruncated(false);
        setExpandedPreviewText('');
        return;
      }

      const fullText = getPlainTextFromRichText(data);
      const availableWidth =
        node.getBoundingClientRect().width || node.parentElement?.getBoundingClientRect().width || 0;

      if (!fullText) {
        setExpandedPreviewText('');
        setIsOverflowing(false);
        setIsExpandedTruncated(false);
        return;
      }

      if (!availableWidth || typeof document === 'undefined') {
        setExpandedPreviewText(fullText);
        setIsOverflowing(false);
        setIsExpandedTruncated(false);
        return;
      }

      const maxExpandedPreviewLines = Math.floor(maxPreviewHeight / expandedLineHeightPx);
      const maxMeasuredHeight = maxExpandedPreviewLines * expandedLineHeightPx;

      const applyMeasuredTextStyles = (element) => {
        element.style.boxSizing = 'border-box';
        element.style.width = `${availableWidth}px`;
        element.style.fontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';
        element.style.fontSize = '12px';
        element.style.lineHeight = `${expandedLineHeightPx}px`;
        element.style.fontWeight = '400';
        element.style.letterSpacing = '0.03em';
        element.style.whiteSpace = 'pre-wrap';
        element.style.overflowWrap = 'break-word';
        element.style.wordBreak = 'normal';
      };

      const measureHeight = (text, includeReadMore) => {
        const measureNode = document.createElement('div');

        measureNode.style.position = 'fixed';
        measureNode.style.left = '-9999px';
        measureNode.style.top = '0';
        measureNode.style.visibility = 'hidden';
        measureNode.style.pointerEvents = 'none';

        applyMeasuredTextStyles(measureNode);

        measureNode.appendChild(document.createTextNode(text));

        if (includeReadMore) {
          measureNode.appendChild(document.createTextNode(inlineReadMorePrefix));

          const readMoreNode = document.createElement('span');
          readMoreNode.textContent = 'Read more';
          applyMeasuredTextStyles(readMoreNode);
          readMoreNode.style.width = 'auto';
          readMoreNode.style.whiteSpace = 'nowrap';

          measureNode.appendChild(readMoreNode);
        }

        document.body.appendChild(measureNode);
        const height = measureNode.scrollHeight;
        measureNode.remove();

        return height;
      };

      const normalizedFullText = fullText.trimEnd();
      const fullTextHeight = measureHeight(normalizedFullText, false);

      if (fullTextHeight <= maxMeasuredHeight + 1) {
        setExpandedPreviewText(normalizedFullText);
        setIsOverflowing(false);
        setIsExpandedTruncated(false);
        return;
      }

      let low = 0;
      let high = normalizedFullText.length;
      let bestFit = '';

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = trimToWordBoundary(normalizedFullText.slice(0, mid));

        if (!candidate) {
          low = mid + 1;
          continue;
        }

        const candidateHeight = measureHeight(candidate, true);

        if (candidateHeight <= maxMeasuredHeight + 1) {
          bestFit = candidate;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      let normalizedBestFit = bestFit.trimEnd();

      while (normalizedBestFit && measureHeight(normalizedBestFit, true) > maxMeasuredHeight + 1) {
        normalizedBestFit = trimToWordBoundary(normalizedBestFit.slice(0, -1));
      }

      setExpandedPreviewText(normalizedBestFit);
      setIsOverflowing(true);
      setIsExpandedTruncated(Boolean(normalizedBestFit));
    };

    checkOverflow();
    const rafId = window.requestAnimationFrame(checkOverflow);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && previewRef.current) {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(previewRef.current);
    }

    window.addEventListener('resize', checkOverflow);
    return () => {
      window.cancelAnimationFrame(rafId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', checkOverflow);
    };
  }, [data, isExpandedView, maxPreviewHeight]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
      <Box
        ref={previewRef}
        sx={{
          width: '100%',
          maxHeight: `${maxPreviewHeight}px`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {isExpandedView ? (
          <Typography
            component="div"
            sx={{
              textAlign: 'left',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: `${expandedLineHeightPx}px`,
              letterSpacing: '0.03em',
              color: '#000000',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word',
              wordBreak: 'normal',
            }}
          >
            {expandedPreviewText}
            {isExpandedTruncated ? (
              <>
                {inlineReadMorePrefix}
                <MuiLink
                  component="button"
                  type="button"
                  underline="always"
                  sx={{
                    display: 'inline',
                    p: 0,
                    border: 'none',
                    backgroundColor: 'transparent',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: `${expandedLineHeightPx}px`,
                    letterSpacing: '0.03em',
                    verticalAlign: 'baseline',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={onReadMore}
                >
                  Read more
                </MuiLink>
              </>
            ) : null}
          </Typography>
        ) : (
          <RichTextEditor defaultValue={''} value={data} readOnly={true} />
        )}
      </Box>

      {isOverflowing && !isExpandedView ? (
        <MuiLink
          component="button"
          type="button"
          underline="always"
          sx={{
            marginTop: '8px',
            alignSelf: 'flex-start',
            p: 0,
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '16px',
            letterSpacing: '0.03em',
          }}
          onClick={onReadMore}
        >
          Read more
        </MuiLink>
      ) : null}
    </Box>
  );
};

const readMoreDialogFieldEyebrowSx = {
  ...queryAdderPopupEyebrowSx,
  color: neutralPalette.textSecondary,
  fontSize: '11px',
  lineHeight: 1.2,
  letterSpacing: '0.16em',
};

const readMoreDialogTitleSx = {
  ...adderHeaderTitleSx,
  fontSize: '1.25rem',
  lineHeight: 1.6,
  whiteSpace: 'normal',
  overflow: 'visible',
  textOverflow: 'clip',
  maxWidth: '100%',
  color: neutralPalette.black,
};

const readMoreDialogConfigByType = {
  [TrackerDataType.SERVICE]: {
    eyebrow: 'SERVICE LOG',
    logColor: logPalette.service.main,
    logHoverColor: logPalette.service.dark,
    Icon: ServiceLogIcon,
  },
  [TrackerDataType.MEDIA_INTERACTION]: {
    eyebrow: 'MEDIA INTERACTIONS',
    logColor: logPalette.mediaInteraction.main,
    logHoverColor: logPalette.mediaInteraction.dark,
    Icon: MediaInteractionLogIcon,
  },
  [TrackerDataType.ENGAGEMENT]: {
    eyebrow: 'ENGAGEMENTS',
    logColor: logPalette.engagement.main,
    logHoverColor: logPalette.engagement.dark,
    Icon: EngagementLogIcon,
  },
  [TrackerDataType.CONTENT_LOG]: {
    eyebrow: 'CONTENT LOG',
    logColor: logPalette.content.main,
    logHoverColor: logPalette.content.dark,
    Icon: ContentLogIcon,
  },
};

const renderContactInfo = (obj, styles) => {
  const iconSize = { fontSize: '20px' };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Typography variant="body2" sx={styles}>
        {obj.name}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ClickToCopyTooltip content={obj.phone} icon={<PhoneIcon style={iconSize} />} />
        <ClickToCopyTooltip content={obj.cell} icon={<SmartphoneIcon style={iconSize} />} />
        <ClickToCopyTooltip content={obj.email} icon={<EmailIcon style={iconSize} />} />
        <ClickToCopyTooltip content={obj.notes} icon={<CommentIcon style={iconSize} />} />
      </Box>
    </Box>
  );
};

const CellViewerTextDialog = ({ payload, open, onClose }) => {
  const canEditFullEntry = typeof payload?.onEditFullEntry === 'function';
  const dialogConfig = readMoreDialogConfigByType[payload?.type] ?? readMoreDialogConfigByType[TrackerDataType.SERVICE];
  const HeaderIcon = dialogConfig.Icon;
  const handleEditFullEntry = () => {
    onClose();
    if (canEditFullEntry) {
      window.setTimeout(() => {
        payload.onEditFullEntry();
      }, 0);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={() => onClose()} PaperProps={{ sx: queryAdderPopupPaperSx }}>
      <DialogTitle
        sx={{
          ...queryAdderPopupHeaderSx,
          borderBottom: 'none',
          pt: '24px !important',
          pb: '0 !important',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.4, minWidth: 0 }}>
          <Box sx={{ ...adderHeaderIconTileSx(dialogConfig.logColor), mt: '1px' }}>
            <HeaderIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={readMoreDialogFieldEyebrowSx}>{dialogConfig.eyebrow}</Typography>
            <Typography sx={readMoreDialogTitleSx}>{payload.title || 'Untitled'}</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          ...queryAdderPopupContentSx,
          pt: '24px !important',
          pb: '24px !important',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            padding: '1.25rem',
            borderRadius: '16px',
            borderColor: neutralPalette.borderMuted,
            boxShadow: 'none',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15 }}>
            <Typography sx={readMoreDialogFieldEyebrowSx}>{payload.fieldLabel}</Typography>
            <RichTextEditor
              defaultValue={''}
              value={payload.content}
              readOnly={true}
              quillStyle={{ resize: 'vertical', fontSize: '13px' }}
            />
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions
        sx={{
          ...utilityModalFooterSx,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.25,
          borderTop: 'none',
          pt: '0 !important',
          pb: '24px !important',
        }}
      >
        <Button variant="outlined" onClick={() => onClose()} startIcon={<CloseIcon />} sx={utilityCancelButtonSx}>
          Close
        </Button>
        {canEditFullEntry ? (
          <Button
            variant="contained"
            onClick={handleEditFullEntry}
            startIcon={<EditOutlinedIcon />}
            sx={{
              ...utilityPrimaryActionButtonSx,
              backgroundColor: logPalette.service.main,
              '&:hover': {
                backgroundColor: logPalette.service.dark,
              },
            }}
          >
            Edit Log
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

CellViewerTextDialog.propTypes = {
  payload: PropTypes.shape({
    content: PropTypes.string,
    fieldLabel: PropTypes.string,
    onEditFullEntry: PropTypes.func,
    title: PropTypes.string,
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

LongTextPreview.propTypes = {
  data: PropTypes.string,
  isExpandedView: PropTypes.bool,
  onReadMore: PropTypes.func,
};

// Helper function to create tooltip with icon
const createTooltipWithIcon = (title, icon, mr = 0.5) => (
  <Tooltip title={<Typography variant={'body2'}>{title}</Typography>} TransitionComponent={Zoom} enterDelay={200}>
    <Box component="span" sx={{ mr }}>
      {icon}
    </Box>
  </Tooltip>
);

const hasCellValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return Boolean(value);
};

const getStatusSourceValue = (value) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (value && typeof value === 'object') {
    if (typeof value.name === 'string') {
      return value.name.trim();
    }
    if (typeof value._id === 'string') {
      return value._id.trim();
    }
  }
  return '';
};

const normalizeStatusValue = (value) => {
  const rawValue = getStatusSourceValue(value);
  if (!rawValue) {
    return value;
  }

  const normalizedLookup = rawValue.toLowerCase();
  if (normalizedLookup === 'complete' || normalizedLookup === 'completed') {
    return ServiceOrIssueStatus.COMPLETED;
  }
  if (normalizedLookup === 'in progress') {
    return ServiceOrIssueStatus.IN_PROGRESS;
  }
  if (normalizedLookup === 'participation ribbon') {
    return ServiceOrIssueStatus.PARTICIPATION_RIBBON;
  }
  if (normalizedLookup === 'cancelled') {
    return ServiceOrIssueStatus.CANCELLED;
  }
  if (normalizedLookup === 'autotracker draft') {
    return ServiceOrIssueStatus.DRAFTED_AUTOTRACKER;
  }

  return rawValue;
};

const getStatusChipLabel = (value) => {
  const normalizedValue = normalizeStatusValue(value);
  if (normalizedValue === ServiceOrIssueStatus.PARTICIPATION_RIBBON) {
    return 'Participation';
  }
  return normalizedValue;
};

const logViewTypeChipLabelSx = {
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: '16px',
  letterSpacing: '0.03em',
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
};

const logViewTypeChipSx = {
  borderRadius: '8px',
  minHeight: '32px',
  height: '32px',
  maxWidth: '100%',
  '& .MuiChip-label': {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    px: 1.75,
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.2,
  },
};

const getFirstExistingCellValue = (cells, keys) => {
  if (!keys) {
    return null;
  }
  const keyList = Array.isArray(keys) ? keys : [keys];
  for (const key of keyList) {
    if (hasCellValue(cells?.[key])) {
      return cells[key];
    }
  }
  return null;
};

// Helper function to create a topic box with proper styling
const createTopicBox = (
  content,
  color,
  fileKey,
  linkedServiceKey,
  cells,
  { fileTooltipText, linkedServiceTooltipText, autotrackerTooltipText } = {}
) => {
  const fileValue = getFirstExistingCellValue(cells, fileKey);
  const linkedServiceValue = getFirstExistingCellValue(cells, linkedServiceKey);
  const hasAutotracker =
    cells[IssueAndServiceSharedValues.autotracker] &&
    Object.keys(cells[IssueAndServiceSharedValues.autotracker]).length > 0;

  return (
    <Box sx={{ paddingLeft: '6px' }}>
      <Typography
        variant="body2"
        sx={{
          fontFamily: '"Open Sans", Arial, sans-serif',
          textAlign: 'left',
          color,
          fontSize: '13px',
          fontWeight: 600,
          lineHeight: '16px',
          letterSpacing: 'normal',
        }}
      >
        {content}
      </Typography>
      {(fileKey || linkedServiceKey) && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          {fileValue ? createTooltipWithIcon(fileTooltipText, <AttachmentIcon />) : null}
          {linkedServiceValue ? createTooltipWithIcon(linkedServiceTooltipText, <LinkedServiceIcon />, 0) : null}
          {hasAutotracker ? createTooltipWithIcon(autotrackerTooltipText, <MadeByAutoTrackerIcon />, 0) : null}
        </Box>
      )}
    </Box>
  );
};

export default function CellViewer({
  index,
  cells,
  dropdownTable = {},
  callback,
  openForm = () => {},
  openSecondaryForm = () => {},
  openTertiaryForm = () => {},
  id,
  classes = {},
  type,
  handleDrag,
  handleDrop,
  isDragActive,
  isExpandedView = false,
  isSharedResource = false,
  onEditItem,
  commsMaterialActions,
  onOpenFullEntryEditor,
}) {
  let cellData;
  const findExpertName = (id) => {
    return dropdownTable['expert']?.find?.((expert) => expert['_id'] === id)?.name ?? id;
  };

  const dispatch = useFiltersDispatch();
  const { openDialog } = useDialog();
  const collapsedFontBump = '0.5pt';
  const collapsedBodyFontSize = isExpandedView ? '12px' : `calc(12px + ${collapsedFontBump})`;
  const collapsedChipFontSize = isExpandedView ? '11px' : `calc(11px + ${collapsedFontBump})`;
  const expandedBodyTextSx = {
    textAlign: 'left',
    fontSize: collapsedBodyFontSize,
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '0.03em',
    color: '#000000',
  };
  const rowChipLabelSx = {
    ...logViewTypeChipLabelSx,
    fontSize: collapsedChipFontSize,
  };
  const rowChipSx = {
    ...logViewTypeChipSx,
    '& .MuiChip-label': {
      ...logViewTypeChipSx['& .MuiChip-label'],
      fontSize: collapsedChipFontSize,
    },
  };
  const mediaLinkedLogAddIconColor = logPalette.mediaInteraction.main;
  const engagementLinkedLogAddIconColor = `${getTrackerViewColor(TrackerDataType.ENGAGEMENT)}.main`;
  const contentLinkedLogAddIconColor = `${getTrackerViewColor(TrackerDataType.CONTENT_LOG)}.main`;
  const serviceLinkedLogAddIconColor = logPalette.service.main;

  const leftAlignedStyle = {
    paddingLeft: isExpandedView ? 0 : '6px',
    ...expandedBodyTextSx,
  };
  const centeredContactStyle = {
    ...expandedBodyTextSx,
    textAlign: 'center',
    paddingLeft: 0,
  };

  const normalizeEngagementCommunityMembers = (communityMembers, dropdownTable) => {
    if (!communityMembers) return [];
    const ids = Array.isArray(communityMembers) ? communityMembers : [communityMembers];
    if (!dropdownTable?.communityMember) return ids;
    return ids.map((id) => dropdownTable.communityMember.find((cm) => cm._id === (id?._id ?? id))).filter(Boolean);
  };

  const normalizeOrganizations = (organizations, dropdownTable) => {
    if (!organizations) return [];
    const items = Array.isArray(organizations) ? organizations : [organizations];
    return items
      .map((item) => {
        if (item?.name) {
          return item;
        }
        if (dropdownTable?.organization) {
          const id = item?._id ?? item;
          return dropdownTable.organization.find((org) => org._id === id);
        }
        return item;
      })
      .filter(Boolean);
  };

  const contactMethodLabelMap = {
    [ContactMethodName.NO_INTERMEDIARY]: 'in person',
    [ContactMethodName.PHONE]: 'phone call',
  };

  const getContactMethodLabel = (value) => contactMethodLabelMap[value] || value || '';

  const getLinkedContentLogDisplayLabel = (contentLog) => {
    const contentTypeName =
      contentLog?.contentTypeName ||
      (typeof contentLog?.contentType === 'object' ? contentLog?.contentType?.name : contentLog?.contentType);
    if (typeof contentTypeName === 'string' && contentTypeName.trim()) {
      return contentTypeName.trim();
    }

    const rawTypeNames = Array.isArray(contentLog?.typeNames) ? contentLog.typeNames : [];
    const typeNames = rawTypeNames.filter(
      (name) => typeof name === 'string' && name.trim().toLowerCase() !== 'content'
    );
    if (typeNames.length > 0) {
      return typeNames.join(', ');
    }

    const topic = contentLog?.topic?.trim?.();
    if (topic) {
      return topic;
    }

    return 'Content';
  };

  switch (index) {
    case IssueKeys.contactMethod:
    case EngagementKeys.contactMethod:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {getContactMethodLabel(cells[index])}
        </Typography>
      );
      break;
    case IssueAndServiceSharedValues.leadExpert:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {cells[index]?.map((item) => (item.name ? item.name : findExpertName(item)))?.join(', ') ?? cells[index]}
        </Typography>
      );
      break;
    case IssueAndServiceSharedValues.teamMembers:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {cells[index]
            ?.map?.((c) => {
              if (c?.name) {
                return c?.name;
              }
              const user = dropdownTable['lead']?.find((lead) => lead['_id'] === c);
              return user ? user.name : null;
            })
            .join(', \n') ?? dropdownTable['lead']?.find?.((lead) => lead['_id'] === cells[index])?.name}
        </Typography>
      );
      break;
    case IssueAndServiceSharedValues.entryDate:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {dayjs(cells[index]).format('M/D/YYYY')}
        </Typography>
      );
      break;
    case ServiceKeys.dueDate:
    case IssueKeys.dueDate:
      if (!cells[index]) {
        cellData = null;
        break;
      }
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {dayjs(cells[index]).format('M/D/YYYY')}
        </Typography>
      );
      break;
    case IssueKeys.journalist: {
      let obj;
      if (isSharedResource) {
        obj = cells[index];
      } else {
        obj = dropdownTable['journalist']?.find((journalist) => journalist['_id'] === cells[index]);
      }
      cellData = obj ? renderContactInfo(obj, centeredContactStyle) : null;
      break;
    }
    case EngagementKeys.communityMember: {
      const communityMembers = normalizeEngagementCommunityMembers(cells[index], dropdownTable);
      if (communityMembers.length === 0) {
        cellData = null;
        break;
      }
      cellData = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
          {communityMembers.map((communityMember) => (
            <Box key={communityMember._id || communityMember.id}>
              {renderContactInfo(communityMember, centeredContactStyle)}
            </Box>
          ))}
        </Box>
      );
      break;
    }
    case ContentLogKeys.audience: {
      const audienceIds = Array.isArray(cells[index]) ? cells[index] : [cells[index]];
      const audienceRows = (dropdownTable?.audience || []).filter((audience) =>
        audienceIds.some((candidate) => (candidate?._id ?? candidate) === audience._id)
      );

      if (audienceRows.length === 0) {
        cellData = null;
        break;
      }

      cellData = (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'flex-start',
            paddingLeft: isExpandedView ? 0 : '6px',
          }}
        >
          {audienceRows.map((audience, i) => (
            <Box
              key={`${audience?.name || audience}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
                overflow: 'hidden',
                width: 'fit-content',
              }}
            >
              <Box
                sx={{
                  width: '7px',
                  height: '30px',
                  bgcolor: audience?.backgroundColor || '#9E9E9E',
                  mr: 0.8,
                  borderRadius: '3px',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ ...expandedBodyTextSx, textAlign: 'left', paddingLeft: 0 }}>
                {audience?.name || ''}
              </Typography>
            </Box>
          ))}
        </Box>
      );
      break;
    }
    case ContentLogKeys.contentType: {
      const contentTypeRawValue = cells[index];
      if (!contentTypeRawValue) {
        cellData = null;
        break;
      }

      const fallbackContentType = typeof contentTypeRawValue === 'string' ? { name: contentTypeRawValue } : null;
      const matchedContentType = (dropdownTable?.contentType || []).find(
        (contentTypeOption) =>
          contentTypeOption?._id === (contentTypeRawValue?._id ?? contentTypeRawValue) ||
          contentTypeOption?.name === contentTypeRawValue
      );
      const contentTypeObj = contentTypeRawValue?.name
        ? contentTypeRawValue
        : matchedContentType || fallbackContentType;

      if (!contentTypeObj?.name) {
        cellData = null;
        break;
      }

      const chipLabel = contentTypeObj.name;
      const rawChipColor =
        typeof contentTypeObj.backgroundColor === 'string' ? contentTypeObj.backgroundColor.trim() : '';
      const workTypeColor = WORK_TYPE_COLORS.find((c) => c.solid === rawChipColor);

      cellData = (
        <Box sx={{ display: 'flex', paddingLeft: isExpandedView ? 0 : '6px' }}>
          <Chip
            label={<Box sx={rowChipLabelSx}>{chipLabel}</Box>}
            variant="filled"
            title={chipLabel}
            sx={{
              backgroundColor: workTypeColor ? workTypeColor.light : undefined,
              border: 'none',
              ...rowChipSx,
              '& .MuiChip-label': {
                ...rowChipSx['& .MuiChip-label'],
                color: neutralPalette.textPrimary,
              },
            }}
          />
        </Box>
      );
      break;
    }
    case IssueKeys.outlet:
      if (isSharedResource) {
        const obj = cells[index];
        cellData = obj ? (
          <Typography variant="body2" sx={leftAlignedStyle}>
            {obj.name}
          </Typography>
        ) : null;
        break;
      }
    // eslint-disable-next-line no-fallthrough
    case inProgressKeys.outlet:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {dropdownTable['outlet']?.find((org) => org['_id'] === cells[index])?.name ?? null}
        </Typography>
      );
      break;
    case EngagementKeys.organization: {
      const organizations = normalizeOrganizations(cells[index], dropdownTable);
      if (!organizations.length) {
        cellData = null;
        break;
      }
      const display = organizations
        .map((org) => org.name || '')
        .filter(Boolean)
        .join(', ');

      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {display}
        </Typography>
      );
      break;
    }
    case IssueAndServiceSharedValues.type: {
      const typeColorMap = {
        'Red': '#FFBDAD',
        'Green': '#B3F5FF',
        'None': undefined,
      };

      cellData = (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'flex-start',
            paddingLeft: isExpandedView ? 0 : '6px',
          }}
        >
          {cells[index].map((c, i) => {
            const chipLabel = c?.name || '';
            const chipColor = typeColorMap[c.backgroundColor];

            return (
              <Chip
                key={`${chipLabel}-${i}`}
                label={<Box sx={rowChipLabelSx}>{chipLabel}</Box>}
                variant="filled"
                title={chipLabel}
                sx={{
                  backgroundColor: chipColor,
                  ...rowChipSx,
                }}
              />
            );
          })}
        </Box>
      );
      break;
    }
    case IssueKeys.linkedService: {
      const linkedServices = Array.isArray(cells[index]) ? cells[index] : [];
      const hasLinkedService = linkedServices.length > 0;
      let payload;
      if (type === TrackerDataType.MEDIA_INTERACTION) {
        payload = {
          linkedInteraction: {
            _id: id,
          },
        };
      } else if (type === TrackerDataType.ENGAGEMENT) {
        payload = {
          linkedEngagement: {
            _id: id,
          },
        };
      } else if (type === TrackerDataType.CONTENT_LOG) {
        payload = {
          linkedContentLog: {
            _id: id,
          },
        };
      }
      cellData = (
        <Box className={classes.stackBox} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {hasLinkedService && (
            <MuiLink
              component={Link}
              to={RouteInformation.serviceLog.path}
              onClick={() => {
                dispatch({
                  type: 'setFilters',
                  payload,
                });
              }}
              sx={{ color: logPalette.service.main }}
            >
              View Linked Service
            </MuiLink>
          )}
          <Box className={classes.stackBox} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {linkedServices.map((c, idx) => (
              <Typography
                key={c?._id || c?.id || c?.name || `linked-service-${idx}`}
                sx={{ ...expandedBodyTextSx, textAlign: 'center' }}
              >
                {c.name}
              </Typography>
            ))}
          </Box>
          {!hasLinkedService && (
            <Tooltip title={'Create a new Linked Service'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                onClick={() => openForm()}
                data-cy="add-new-linked-service-button-CellViewer"
              >
                <AddCircleIcon sx={{ color: serviceLinkedLogAddIconColor }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
      break;
    }
    case IssueAndServiceSharedValues.campaign: {
      let sanitizedData = Array.isArray(cells[index]) ? cells[index] : [cells[index]];

      if (sanitizedData.length === 0) {
        cellData = <Typography variant="body2" sx={leftAlignedStyle}></Typography>;
      } else {
        cellData = (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'flex-start',
              paddingLeft: isExpandedView ? 0 : '6px',
            }}
          >
            {sanitizedData.map((campaign, i) => (
              <Box
                key={`${campaign?.name || campaign}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  width: 'fit-content',
                }}
              >
                <Box
                  sx={{
                    width: '7px',
                    height: '30px',
                    bgcolor: campaign?.backgroundColor || '#9E9E9E',
                    mr: 0.8,
                    borderRadius: '3px 3px 3px 3px',
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ ...expandedBodyTextSx, textAlign: 'left', paddingLeft: 0 }}>
                  {campaign?.name || campaign}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      }

      break;
    }
    case IssueAndServiceSharedValues.department:
    case IssueAndServiceSharedValues.unit: {
      let display = '';

      if (cells[index] instanceof Array && cells[index].length > 0) {
        display = cells[index]?.map?.((c) => `${c}`)?.join(', \n');
      } else if (typeof cells[index] === 'string') {
        display = cells[index];
      }

      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {display}
        </Typography>
      );
      break;
    }
    case ServiceKeys.linkedInteraction: {
      cellData =
        cells[index]?.interactions?.length > 0 ? (
          <Box className={classes.stackBox} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Link
              to={RouteInformation.mediaInteraction.path}
              onClick={() => {
                dispatch({
                  type: 'setFilters',
                  payload: {
                    linkedService: [
                      {
                        _id: cells[index]._id,
                        name: cells[index].name,
                        firstLetter: cells[index].name.charAt(0),
                      },
                    ],
                  },
                });
              }}
            >
              View Linked Interactions
            </Link>
            {cells[index].interactions.map((c) => (
              <Typography key={c.outlet} sx={expandedBodyTextSx}>
                {c.outlet}
              </Typography>
            ))}
            <Tooltip title={'Add an interaction'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                sx={{ alignSelf: 'center' }}
                onClick={() => openForm()}
                data-cy="add-new-linked-interaction-button-CellViewer"
              >
                <AddCircleIcon sx={{ color: mediaLinkedLogAddIconColor }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box className={classes.stackBox} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={{ ...expandedBodyTextSx, textAlign: 'center' }}>No Media Interactions Yet</Typography>
            <Tooltip title={'Add an interaction'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                sx={{ alignSelf: 'center' }}
                onClick={() => openForm()}
                data-cy="add-new-linked-interaction-button-CellViewer"
              >
                <AddCircleIcon sx={{ color: mediaLinkedLogAddIconColor }} />
              </IconButton>
            </Tooltip>
          </Box>
        );

      // TODO check placement and icons
      const resolveCommunityMemberNames = (engagement, dropdownTable) => {
        const { communityMembers, communityMember } = engagement;
        if (Array.isArray(communityMembers)) {
          return communityMembers
            .map((member) => {
              if (typeof member === 'string') {
                return dropdownTable.communityMember?.find((cm) => cm._id === member)?.name ?? member;
              }
              return member?.name ?? '';
            })
            .filter(Boolean)
            .join(', ');
        }
        if (communityMember) {
          return communityMember;
        }
        return '';
      };

      const getLinkedEngagementsSummary = (linkedEngagementCell, dropdownTable) => {
        const engagements = linkedEngagementCell?.engagements;
        if (!Array.isArray(engagements)) {
          return [];
        }

        return engagements
          .map((engagement, index) => {
            const names = resolveCommunityMemberNames(engagement, dropdownTable);
            if (!names) {
              return null;
            }

            return {
              key: engagement._id ?? engagement.id ?? `${names}-${index}`,
              text: names,
            };
          })
          .filter(Boolean);
      };

      const linkedEngagementsSummary = getLinkedEngagementsSummary(cells[ServiceKeys.linkedEngagement], dropdownTable);

      const engagementCellData =
        cells[ServiceKeys.linkedEngagement]?.engagements?.length > 0 ? (
          <Box className={classes.stackBox} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Link
              to={RouteInformation.engagementLog.path}
              onClick={() => {
                dispatch({
                  type: 'setFilters',
                  payload: {
                    linkedService: [
                      {
                        _id: cells[index]._id,
                        name: cells[index].name,
                        firstLetter: cells[index].name.charAt(0),
                      },
                    ],
                  },
                });
              }}
            >
              View Linked Engagements
            </Link>
            {linkedEngagementsSummary.map((s) => (
              <Typography key={s.key} sx={expandedBodyTextSx}>
                {s.text}
              </Typography>
            ))}
            <Tooltip title={'Add an engagement'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                sx={{ alignSelf: 'center' }}
                onClick={() => openSecondaryForm()}
                data-cy="add-new-linked-engagement-button-CellViewer"
              >
                <AddCircleIcon sx={{ color: engagementLinkedLogAddIconColor }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box className={classes.stackBox} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={{ ...expandedBodyTextSx, textAlign: 'center' }}>No Engagements Yet</Typography>
            <Tooltip title={'Add an engagement'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                sx={{ alignSelf: 'center' }}
                onClick={() => openSecondaryForm()}
                data-cy="add-new-linked-engagement-button-CellViewer"
              >
                <AddCircleIcon sx={{ color: engagementLinkedLogAddIconColor }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      const serviceName = cells[index]?.name || cells[ServiceKeys.service] || '';
      const linkedServicePayload = cells[index]?._id
        ? [
            {
              _id: cells[index]._id,
              name: serviceName,
              firstLetter: serviceName ? serviceName.charAt(0) : '',
            },
          ]
        : [];

      const linkedContentLogs = Array.isArray(cells[ServiceKeys.linkedContentLog]?.contentLogs)
        ? cells[ServiceKeys.linkedContentLog].contentLogs
        : [];
      const linkedContentTypeRows = linkedContentLogs
        .map((contentLog) => getLinkedContentLogDisplayLabel(contentLog))
        .filter(Boolean);

      const contentCellData =
        linkedContentLogs.length > 0 ? (
          <Box className={classes.stackBox} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Link
              to={RouteInformation.contentLog.path}
              onClick={() => {
                dispatch({
                  type: 'setFilters',
                  payload: {
                    linkedService: linkedServicePayload,
                  },
                });
              }}
            >
              View Content
            </Link>
            {linkedContentTypeRows.map((typeRow, rowIndex) => (
              <Typography key={`${typeRow}-${rowIndex}`} sx={expandedBodyTextSx}>
                {typeRow}
              </Typography>
            ))}
            <Tooltip title={'Add a content log'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                sx={{ alignSelf: 'center' }}
                onClick={() => openTertiaryForm()}
                data-cy="add-new-linked-content-button-CellViewer"
              >
                <AddCircleIcon sx={{ color: contentLinkedLogAddIconColor }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box className={classes.stackBox} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={{ ...expandedBodyTextSx, textAlign: 'center' }}>No Content Items Yet</Typography>
            <Tooltip title={'Add a content log'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                sx={{ alignSelf: 'center' }}
                onClick={() => openTertiaryForm()}
                data-cy="add-new-linked-content-button-CellViewer"
              >
                <AddCircleIcon sx={{ color: contentLinkedLogAddIconColor }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      cellData = (
        <>
          {cellData}
          <AddOnProtectedWrapper addOnType={AddOnTypes.engagementLog}>{engagementCellData}</AddOnProtectedWrapper>
          <AddOnProtectedWrapper addOnType={AddOnTypes.contentLog}>{contentCellData}</AddOnProtectedWrapper>
        </>
      );

      break;
    }
    case IssueKeys.topic:
      if (type === TrackerDataType.CONTENT_LOG) {
        cellData = createTopicBox(
          cells[index],
          'black',
          [ContentLogKeys.file, 'files'],
          [
            ContentLogKeys.linkedService,
            IssueAndEngagementSharedValues.linkedService,
            'linkedServices',
            'linkedService',
          ],
          cells,
          {
            fileTooltipText: 'Content Log has attached drafts, images, or links',
            linkedServiceTooltipText: 'Content Log has been linked to a Service',
          }
        );
      } else if (type === TrackerDataType.ENGAGEMENT) {
        cellData = createTopicBox(
          cells[index],
          'black',
          EngagementKeys.file,
          IssueAndEngagementSharedValues.linkedService,
          cells,
          {
            fileTooltipText: 'Engagement has attached links or documents',
            linkedServiceTooltipText: 'Engagement has been linked to a Service',
          }
        );
      } else {
        cellData = createTopicBox(
          cells[index],
          'black',
          IssueKeys.file,
          IssueAndEngagementSharedValues.linkedService,
          cells,
          {
            fileTooltipText: 'Interaction has attached links',
            linkedServiceTooltipText: 'Interaction has been linked to a Service',
            autotrackerTooltipText: 'Interaction has been created through AutoTracker',
          }
        );
      }
      break;
    case ServiceKeys.service:
      cellData = createTopicBox(cells[index], 'black', ServiceKeys.file, null, cells, {
        fileTooltipText: 'Service has attached links or documents',
        autotrackerTooltipText: 'Service has been created through AutoTracker',
      });
      break;
    case IssueAndServiceSharedValues.backgroundResponse:
    case EngagementKeys.background:
    case ContentLogKeys.background:
    case ContentLogKeys.content:
    case ContentLogKeys.otherNotes:
    case EngagementKeys.notes:
    case IssueAndServiceSharedValues.keyMessaging: {
      const addonKey =
        index === IssueAndServiceSharedValues.backgroundResponse || index === EngagementKeys.background
          ? 'response'
          : 'keyMessaging';

      let data = cells[index] ? cells[index] : '';
      if (
        (type === TrackerDataType.MEDIA_INTERACTION || type === TrackerDataType.ENGAGEMENT) &&
        cells[IssueAndEngagementSharedValues.fieldAddons]?.[addonKey]
      ) {
        data = `${data !== '' ? data + '\n' : ''}---- Copied from Linked Service ---- \n${
          cells[IssueAndEngagementSharedValues.fieldAddons][addonKey]
        }`;
      }

      const dialogTitle =
        type === TrackerDataType.SERVICE ? cells[ServiceKeys.service] : cells[IssueAndEngagementSharedValues.topic];
      const openReadMoreDialog = () =>
        openDialog(CellViewerTextDialog, {
          type,
          title: dialogTitle,
          fieldLabel: index,
          content: data,
          onEditFullEntry: onOpenFullEntryEditor,
        });

      const displayComponent = (
        <LongTextPreview data={data} isExpandedView={isExpandedView} onReadMore={openReadMoreDialog} />
      );

      cellData = (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
          {displayComponent}
        </div>
      );
      break;
    }
    case IssueKeys.file:
      cellData = (
        <UploadCell
          id={id}
          cellName={index}
          files={cells[index]}
          callback={callback}
          type={type}
          onEditItem={onEditItem}
          popupType={isExpandedView && type === TrackerDataType.MEDIA_INTERACTION ? type : undefined}
          dropzoneStyleType={isExpandedView && type === TrackerDataType.MEDIA_INTERACTION ? type : undefined}
          hideEmptyDropzone={isExpandedView && type === TrackerDataType.MEDIA_INTERACTION}
          compactCardGrid={isExpandedView}
        />
      );
      break;
    case ServiceKeys.file:
    case EngagementKeys.file:
      cellData = (
        <UploadCell
          id={id}
          cellName={index}
          files={cells[index]}
          type={type}
          callback={callback}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          isDragActive={isDragActive}
          onEditItem={onEditItem}
          dropzoneStyleType={
            isExpandedView && (type === TrackerDataType.SERVICE || type === TrackerDataType.ENGAGEMENT)
              ? type
              : undefined
          }
          emptyDropzoneActions={commsMaterialActions}
          compactCardGrid={isExpandedView}
        />
      );
      break;
    case ContentLogKeys.file:
      cellData = (
        <UploadCell
          id={id}
          cellName={index}
          files={cells[index]}
          callback={callback}
          type={type}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          isDragActive={isDragActive}
          onEditItem={onEditItem}
          dropzoneStyleType={isExpandedView && type === TrackerDataType.CONTENT_LOG ? type : undefined}
          emptyDropzoneActions={commsMaterialActions}
          compactCardGrid={isExpandedView}
        />
      );
      break;
    case ContentLogKeys.liveLink:
      if (isExpandedView && !cells[index]) {
        cellData = null;
        break;
      }
      cellData = (
        <UploadCell
          id={id}
          cellName={index}
          files={cells[index] ? [{ ...cells[index], type: 'url' }] : []}
          callback={(updatedFiles) => callback(updatedFiles?.[0] ?? null, index)}
          type={type}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          isDragActive={isDragActive}
          onEditItem={onEditItem}
          popupType={type}
          compactCardGrid={isExpandedView}
        />
      );
      break;
    case ServiceKeys.complexity:
      if (cells[index]) {
        const complexityOption = complexityOptions.find((option) => option._id === cells[index]);
        cellData = complexityOption ? (
          <Chip
            label={complexityOption._id}
            variant="filled"
            sx={{
              '& .MuiChip-label': {
                padding: 0,
              },
            }}
          />
        ) : (
          cells[index]
        );
      } else {
        cellData = '';
      }
      break;
    case IssueKeys.tone: {
      const toneIcons = {
        [mediaToneOptions[0]._id]: <PositiveIcon color="#216A35" />,
        [mediaToneOptions[1]._id]: <NeutralIcon color="#111827" />,
        [mediaToneOptions[2]._id]: <NegativeIcon color="#912018" />,
      };

      cellData = (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {toneIcons[cells[index]] || ''}
        </Box>
      );
      break;
    }
    // temporarily hidden
    // case ServiceKeys.diversity:
    //   cellData = (
    //     <Typography variant="body2" style={{ minWidth: '150px' }}>
    //       {cells[index]}
    //     </Typography>
    //   );
    //   break;
    case IssueAndServiceSharedValues.status: {
      const normalizedStatusValue = normalizeStatusValue(cells[index]);
      cellData = (
        <Chip
          label={getStatusChipLabel(normalizedStatusValue) || ''}
          color={ServiceOrIssueStatusColorMap[normalizedStatusValue] || undefined}
          variant="filled"
          sx={rowChipSx}
        />
      );
      break;
    }
    default:
      cellData = cells[index] ?? '';
      break;
  }
  return <ErrorBoundary>{cellData}</ErrorBoundary>;
}

CellViewer.propTypes = {
  index: PropTypes.string.isRequired,
  cells: PropTypes.object.isRequired,
  dropdownTable: PropTypes.shape({
    expert: PropTypes.array,
    communityMember: PropTypes.array,
    organization: PropTypes.array,
    audience: PropTypes.array,
    lead: PropTypes.array,
    journalist: PropTypes.array,
    outlet: PropTypes.array,
  }),
  callback: PropTypes.func,
  openForm: PropTypes.func,
  openSecondaryForm: PropTypes.func,
  openTertiaryForm: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  classes: PropTypes.shape({
    stackBox: PropTypes.string,
    addLinkedButton: PropTypes.string,
  }),
  type: PropTypes.string,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
  isExpandedView: PropTypes.bool,
  isSharedResource: PropTypes.bool,
  onEditItem: PropTypes.func,
  commsMaterialActions: PropTypes.node,
  onOpenFullEntryEditor: PropTypes.func,
};
